(() => {
  const instances = new Map();
  const sliderUtils = window.AWSliderCoreUtils;
  const selectors = {
    root: "[data-aw-article-related-items]",
    viewport: "[data-aw-article-related-items-viewport]",
    list: "[data-aw-article-related-items-list]",
    item: "[data-aw-article-related-items-item]",
    scrollbar: "[data-aw-article-related-items-scrollbar]",
    thumb: "[data-aw-article-related-items-scrollbar-thumb]",
    prev: "[data-aw-article-related-items-prev]",
    next: "[data-aw-article-related-items-next]",
  };

  class AWArticleRelatedItems {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(selectors.viewport);
      this.list = root.querySelector(selectors.list);
      this.scrollbar = root.querySelector(selectors.scrollbar);
      this.thumb = root.querySelector(selectors.thumb);
      this.prevButton = root.querySelector(selectors.prev);
      this.nextButton = root.querySelector(selectors.next);
      this.rafId = null;
      this.resizeId = null;

      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onImageLoad = this.onImageLoad.bind(this);

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      this.prevButton?.addEventListener("click", this.onPrevClick);
      this.nextButton?.addEventListener("click", this.onNextClick);
      this.viewport?.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize);
      sliderUtils.bindImageLoad(this.root, this.onImageLoad);
    }

    getItemDistance() {
      return sliderUtils.getItemDistance({
        list: this.list,
        itemSelector: selectors.item,
        viewport: this.viewport,
      });
    }

    scroll(direction) {
      if (!this.viewport) {
        return;
      }

      this.viewport.scrollBy({
        left: direction * this.getItemDistance(),
        behavior: sliderUtils.getScrollBehavior(),
      });
    }

    onPrevClick() {
      if (this.prevButton?.getAttribute("aria-disabled") === "true") {
        return;
      }

      this.scroll(-1);
    }

    onNextClick() {
      if (this.nextButton?.getAttribute("aria-disabled") === "true") {
        return;
      }

      this.scroll(1);
    }

    onScroll() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    onResize() {
      if (this.resizeId) {
        return;
      }

      this.resizeId = window.requestAnimationFrame(() => {
        this.resizeId = null;
        this.update();
      });
    }

    onImageLoad() {
      this.update();
    }

    update() {
      if (!this.viewport) {
        return;
      }

      const maxScroll = sliderUtils.getMaxScroll(this.viewport);
      const hasOverflow = maxScroll > 1;
      const scrollLeft = Math.max(0, this.viewport.scrollLeft);

      this.root.classList.toggle("is-static", !hasOverflow);
      sliderUtils.updateButtonState(this.prevButton, !hasOverflow || scrollLeft <= 1);
      sliderUtils.updateButtonState(this.nextButton, !hasOverflow || scrollLeft >= maxScroll - 1);
      sliderUtils.updateScrollbar({
        viewport: this.viewport,
        scrollbar: this.scrollbar,
        thumb: this.thumb,
        enabled: hasOverflow,
      });
    }

    destroy() {
      this.prevButton?.removeEventListener("click", this.onPrevClick);
      this.nextButton?.removeEventListener("click", this.onNextClick);
      this.viewport?.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
      sliderUtils.unbindImageLoad(this.root, this.onImageLoad);

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      if (this.resizeId) {
        window.cancelAnimationFrame(this.resizeId);
        this.resizeId = null;
      }
    }
  }

  function getRoots(container = document) {
    if (container?.matches?.(selectors.root)) {
      return [container];
    }

    return Array.from(container?.querySelectorAll?.(selectors.root) || []);
  }

  function init(container) {
    getRoots(container).forEach((root) => {
      if (!instances.has(root)) {
        instances.set(root, new AWArticleRelatedItems(root));
      }
    });
  }

  function destroy(container) {
    getRoots(container).forEach((root) => {
      const instance = instances.get(root);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }
})();
